import { useState } from "react";
import { MapPin, Phone, Mail, Clock, MessageCircle } from "lucide-react";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";
import { SITE } from "@/lib/seo";
import { toast } from "sonner";

const inputCls = "w-full border border-[#E5E5DF] px-4 py-3 text-sm focus:outline-none focus:border-[#B84A3B] transition-colors bg-[#FFFFFF]";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in all three fields.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent!");
      setForm({ name: "", email: "", message: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send — please email us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#FAFAF7] min-h-[70vh]" data-testid="contact-page">
      <Seo
        title="Contact Us — Kurti Connection USA, Schaumburg IL"
        description="Reach Kurti Connection USA: visit our Schaumburg, IL boutique, call, email or WhatsApp us about orders, sizing, alterations and same-day delivery."
        path="/contact"
      />
      <div className="max-w-[1100px] mx-auto px-5 py-14 sm:py-20">
        <div className="overline text-[#B84A3B]">We'd love to hear from you</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight mt-2">Contact Us</h1>
        <p className="text-sm text-[#6E6E69] mt-3 max-w-xl">
          Questions about an order, sizing, alterations, or same-day delivery? We're a real,
          family-run boutique in Schaumburg, Illinois — talk to a human, not a bot.
        </p>

        <div className="grid md:grid-cols-2 gap-10 mt-12">
          <div className="space-y-6">
            {[
              { icon: MapPin, label: "Visit the boutique", testid: "contact-address",
                body: <>183 West Golf Rd, Schaumburg, IL 60195<br />
                  <a href="https://maps.google.com/?q=183+West+Golf+Rd,+Schaumburg,+IL+60195" target="_blank" rel="noreferrer"
                    data-testid="contact-directions" className="text-[#B84A3B] underline underline-offset-2">Get directions</a></> },
              { icon: Phone, label: "Call or text", testid: "contact-phone",
                body: <a href={`tel:${SITE.phone}`} className="hover:text-[#B84A3B] transition-colors">{SITE.phoneDisplay}</a> },
              { icon: MessageCircle, label: "WhatsApp (fastest)", testid: "contact-whatsapp",
                body: <a href={`https://wa.me/${SITE.phone.replace("+", "")}?text=${encodeURIComponent("Hi Kurti Connection! I have a question.")}`}
                  target="_blank" rel="noreferrer" className="text-[#1A8A45] underline underline-offset-2">Message us on WhatsApp</a> },
              { icon: Mail, label: "Email", testid: "contact-email",
                body: <a href="mailto:hello@kurticonnection.com" className="hover:text-[#B84A3B] transition-colors">hello@kurticonnection.com</a> },
              { icon: Clock, label: "Store hours", testid: "contact-hours",
                body: <>Mon: 2:00 PM – 7:00 PM<br />Tue – Fri: 11:30 AM – 7:00 PM<br />Sat &amp; Sun: 11:30 AM – 6:00 PM</> },
            ].map(({ icon: Icon, label, body, testid }) => (
              <div key={label} className="flex items-start gap-4" data-testid={testid}>
                <div className="w-10 h-10 bg-[#B84A3B]/10 flex items-center justify-center shrink-0">
                  <Icon size={17} className="text-[#B84A3B]" />
                </div>
                <div>
                  <div className="overline text-[#1A1A1A] mb-1">{label}</div>
                  <div className="text-sm text-[#6E6E69] leading-relaxed">{body}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-[#FFFFFF] border border-[#E5E5DF] p-6 sm:p-8 h-fit" data-testid="contact-form">
            <h2 className="font-display text-2xl">Send us a message</h2>
            <div className="space-y-3 mt-5">
              <input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name" className={inputCls} />
              <input data-testid="contact-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Your email" className={inputCls} />
              <textarea data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="How can we help?" rows={5} className={`${inputCls} resize-none`} />
              <button data-testid="contact-submit" onClick={submit} disabled={sending}
                className="w-full bg-[#1A1A1A] text-[#FFFFFF] py-4 text-[0.72rem] font-mono tracking-[0.22em] uppercase hover:bg-[#B84A3B] transition-colors duration-300 disabled:opacity-60">
                {sending ? "Sending…" : "Send Message"}
              </button>
              <p className="text-[0.65rem] font-mono text-[#B0B0A8] text-center">We reply within one business day.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
